import { useState, useEffect } from "react";
import {
  Select,
  Button,
  Menu,
  List,
  Text,
  Flex,
  Input,
  inBetween,
  OverflowText
} from "@theorgchartdev/storybook";
import { SwitchWrapper } from "../../../../Menus/FieldToggleMenu/FieldToggleMenu";
import { createSelectOptions, getFeatureAccess } from "@/Utils/Misc";
import { AllowedLevels, AllChartTypes } from "@/Utils/Constants";
import SectionCard, { SectionCardGroup } from "../../../../Composites/SectionCard/SectionCard";
import * as StateLib from "@/Utils/StateLib";
import ActionListItem from "../../../../Composites/ActionListItem/ActionListItem";
import { BoxSort } from "../../../../../Navigation/RightSidebar/Sort/Sort";
import { LoadDirectory } from "@/Utils/Promises";
import cx from "classnames";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { BuildBackgroundOptions } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/Backgrounds.utils";
import { appstr } from "@coreapp/utils/appstr";
import {
  enumBrand,
  enumChartTypes,
  enumChartTemplateSet,
  enumHierarchyTypes,
  enumFeaturesList
} from "@coreapp/constants";
import { QViewMgr } from "@coreapp/CoreApp/QViewMgr";
import { QSortConfig } from "@coreapp/CoreApp/QSortConfig";
import { GetExtension } from "@coreapp/utils/GetExtension";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import { gWhiteLabel } from "@coreapp/whitelabel/brand";
import {
  ChartSettingsSectionRoutes,
  getChartSettingsTestPrefix
} from "@/providers/NavigationProvider";

export const PRESENTATION_TEST_PREFIX = getChartSettingsTestPrefix(
  ChartSettingsSectionRoutes.Presentation
);
const PRESENTATION_VIEW_TEST_PREFIX = `${PRESENTATION_TEST_PREFIX}-view`;

const SharedSelectProperties = {
  showCheck: false,
  setOpen: () => {},
  optionSize: "small",
  bold: true,
  triggerProps: {
    modifier: "secondary",
    size: "small"
  }
};

export const chartTypeIntToString = cti => {
  if (cti >= 0 && cti < AllChartTypes.length) {
    return AllChartTypes[cti];
  } else {
    return "";
  }
};

export const chartTypeStringToInt = cts => {
  return AllChartTypes.indexOf(cts);
};

export const PresentationConfiguration = ({ chartConfig, handleUpdate }) => {
  const wsMgr = StateLib.GetWSMgr();
  const vm = new QViewMgr();
  vm.InitViewMgr(wsMgr);
  vm.ResetViewMgr();
  const eventTracker = wsMgr.GetUsageTracker();
  const [selectedViewID, setSelectedViewID] = useState(null);
  const [availableBackgrounds, setAvailableBackgrounds] = useState([]);

  useEffect(() => {
    eventTracker.ReportEvent("chart_settings.presentation.opened");
  }, []);

  const LoadBackgrounds = async () => {
    const { success, processedResults } = await LoadDirectory("Masters", "Masters");
    if (success) setAvailableBackgrounds(processedResults);
  };

  useEffect(() => {
    LoadBackgrounds();
  }, []);

  if (!chartConfig) return;

  const sortConfig = new QSortConfig();
  const curCriteria = chartConfig["sort"];
  if (curCriteria) {
    sortConfig.LoadFromJSON(curCriteria);
  }

  const viewsList = chartConfig["views"];
  for (const aView in viewsList) {
    vm.AddView(viewsList[aView]);
  }

  let curDefLevels = chartConfig["defaultlevel"] ?? 2;
  let curChartType = chartConfig["chartType"] ?? enumChartTypes.standard;
  let curBackground = chartConfig["mastertemplate"];
  let chartTypes = AllChartTypes;
  for (const type in chartTypes) {
    if (chartTypes[type] == "Inverted") {
      if (!getFeatureAccess(enumFeaturesList.invertedChart)) {
        chartTypes.splice(type, 1);
      }
    }
    if (chartTypes[type] == "Level") {
      if (!getFeatureAccess(enumFeaturesList.levelChart)) {
        chartTypes.splice(type, 1);
      }
    }
  }

  if (curBackground) {
    let curExt = GetExtension(curBackground);
    if (curExt == null || curExt === "") curBackground = curBackground + ".oc5m";
  } else {
    curBackground = "None";
  }

  const BuildViewOptions = handleClick => {
    const wsMgr = StateLib.GetWSMgr();
    const baseClass = "ocn-field-toggle-option";
    if (!wsMgr) {
      return [];
    }
    var ctm = wsMgr.GetChartTemplatesMgr();
    var sharedTemplates = ctm.GetTemplateList(enumChartTemplateSet.sharedtemplates, false);
    var defSysTemplates = ctm
      .GetTemplateList(enumChartTemplateSet.systemtemplates)
      .filter(function (aTemplate) {
        if (gWhiteLabel === enumBrand.isolved) {
          return aTemplate.fTemplateSet === enumBrand.isolved;
        }
        return aTemplate.fTemplateSet == "ADP";
      });

    let chartHierarchyType = enumHierarchyTypes.employees;
    if (IsPositionChart(chartConfig)) {
      chartHierarchyType = enumHierarchyTypes.positions;
    }

    var totalList = defSysTemplates.concat(sharedTemplates).filter(view => {
      return view.GetHierarchyType() === chartHierarchyType;
    });

    const unusedViews = vm.GetUnusedTemplates(true);
    const unusedIds = unusedViews.map(item => item.GetTemplateID());
    let views = [];
    for (let view of totalList) {
      let viewID = view.GetTemplateID();
      let checked = !unusedIds.includes(viewID);
      let checkedClass = checked && `${baseClass}-label--checked`;
      let option = {};
      option.value = viewID;
      option.clickHandler = handleClick;
      option.label = view.GetTemplateName();
      option.content = (
        <span className={baseClass}>
          <span className={cx(`${baseClass}-label`, checkedClass)}>
            <OverflowText className={"oc-block"} size={"small"}>
              {view.GetTemplateName()}
            </OverflowText>
          </span>
          <span className={`${baseClass}-switch`}>
            <SwitchWrapper
              id={"presentation-view-switch-" + viewID}
              checked={checked}
              option={option}
              onChange={handleClick}
            />
          </span>
        </span>
      );
      option.dataTestId = `presentation-view-option-${viewID}`;
      views.push(option);
    }
    return views;
  };

  const ToggleView = opt => {
    if (!chartConfig.views.includes(opt.value)) handleAddView(opt);
    else {
      HandleChangesToView("delete", opt.value);
    }
  };

  const handleAddView = opt => {
    chartConfig["views"].push(opt.value);
    handleUpdate(chartConfig, "views");
  };

  const setDefaultLevels = opt => {
    chartConfig["defaultlevel"] = opt;
    handleUpdate(chartConfig, "defaultlevel");
  };

  const setChartType = opt => {
    chartConfig["chartType"] = chartTypeStringToInt(opt);
    handleUpdate(chartConfig, "chartType");
  };

  const setLevelField = opt => {
    chartConfig["levelField"] = opt;
    handleUpdate(chartConfig, "levelField");
  };

  const setBackground = opt => {
    let background = opt?.value;
    if (!IsBackgroundFile(background)) {
      background = null;
    }
    chartConfig["mastertemplate"] = background;
    handleUpdate(chartConfig, "mastertemplate");
  };

  const HandleLimitInput = (index, e) => {
    if (e.target.value !== "") {
      let value = e.target.value;
      value = parseInt(value);
      if (isNaN(value)) {
        value = 0;
      }
      value = inBetween(value, 1, Number.POSITIVE_INFINITY);
      chartConfig["maxQueryLimit"][index] = value;
    }
    if (e.target.value === "") chartConfig["maxQueryLimit"][index] = e.target.value;
    handleUpdate(chartConfig, "maxQueryLimit");
  };

  const HandleChangesToView = (e, viewID) => {
    switch (e) {
      case "rename":
        /*
        let chartObj = updatedAccountConfig.charts.find(
          chart => chart.ID === chartID
        );
        setInputValue(chartObj.name);
        setShowNameInput(true);
        */
        break;
      case "set-default":
        chartConfig.svrtemplate = viewID;
        break;
      case "settings":
        break;
      case "delete":
        let viewIdx = chartConfig.views.findIndex(view => view === viewID);
        chartConfig.views.splice(viewIdx, 1);
        if (chartConfig.svrtemplate == viewID) {
          //Reset if we deleted our default view
          chartConfig.svrtemplate = chartConfig["views"][0];
        }
        break;
      default:
        break;
    }
    handleUpdate(chartConfig, "svrtemplate");
    handleUpdate(chartConfig, "views");
  };

  const HandleListSelect = viewID => {
    if (viewID === selectedViewID) setSelectedViewID(null);
    else setSelectedViewID(viewID);
  };

  const handleSortUpdate = sortConfig => {
    let sortJSON = sortConfig.OutputToJSON();
    chartConfig["sort"] = sortJSON["sortConfig"];
    handleUpdate({ sort: sortJSON["sortConfig"] }, "sort");
  };

  const BuildViewList = (views, defaultId, onChange, clickHandler) => {
    return views.map(view => {
      const viewEntry = {
        content: (
          <ActionListItem
            padding="large"
            label={view.fName}
            actions={[
              /*
              {
                iconName: "pen",
                onClick: () => onChange("rename", view.fViewID)
              },
              */
              {
                iconName: view.fViewID === defaultId ? "star-solid" : "star-regular",
                iconProps: { color: "warning" },
                onClick: () => onChange("set-default", view.fViewID),
                disabled: !getFeatureAccess(enumFeaturesList.adminProfileFieldStarring),
                dataTestId:
                  view.fViewID === defaultId
                    ? `${PRESENTATION_VIEW_TEST_PREFIX}-${view.fViewID}-default-active-btn`
                    : `${PRESENTATION_VIEW_TEST_PREFIX}-${view.fViewID}-set-default-btn`
              },
              /*
              {
                iconName: "gear",
                onClick: () => onChange("settings", view.fViewID)
              },
              */
              {
                iconName: "trash-can",
                iconProps: { color: "error" },
                disabled: views.length === 1,
                onClick: () => onChange("delete", view.fViewID),
                dataTestId: `${PRESENTATION_VIEW_TEST_PREFIX}-${view.fViewID}-delete-btn`
              }
            ]}
          />
        ),
        padding: false,
        divider: true,
        value: view.fViewID,
        clickHandler: e => clickHandler(e.value)
      };

      return viewEntry;
    });
  };

  const fieldTrigger = (
    <Button
      modifier="secondary"
      type="button"
      className={"oc-special-secondary"}
      dataTestId={"cs-pres-views-btn"}
      disabled={!getFeatureAccess(enumFeaturesList.promoteViews)}
    >
      {appstr("Choose views")}
    </Button>
  );

  return (
    <SectionCardGroup>
      <SectionCard
        title={appstr("Available views")}
        titleContent={
          <Menu
            dataTestId={PRESENTATION_VIEW_TEST_PREFIX}
            options={BuildViewOptions(ToggleView)}
            trigger={fieldTrigger}
            listProps={{
              enableSearch: true,
              inputProps: { dataTestId: "presentation-view-search" }
            }}
            stayOpenOnOptionSelect={true}
            position="bottom-end"
            listWidth={"large"}
          />
        }
      >
        <List
          options={BuildViewList(
            vm.fViewList,
            chartConfig.svrtemplate,
            HandleChangesToView,
            HandleListSelect
          )}
          listWidth="fill"
          optionSize={"large"}
          showCheck={false}
        />
      </SectionCard>
      <SectionCard title={appstr("Chart background")}>
        <Flex width={"400px"}>
          <Select
            value={curBackground}
            selectSize={"medium"}
            dataTestId={"cs-background-select"}
            listProps={FieldSelectListProps()}
            placeholder={curBackground ?? appstr("Select background")}
            options={BuildBackgroundOptions(
              "view-rule-background-select",
              availableBackgrounds ?? [],
              setBackground
            )}
            {...SharedSelectProperties}
          />
        </Flex>
      </SectionCard>
      <SectionCard title={appstr("General")}>
        <Flex gap={"16px"} direction={"column"}>
          <Flex direction={"column"} width={"400px"}>
            <Select
              selectSize={"medium"}
              label={appstr("Default levels")}
              id={"default-levels-select"}
              dataTestId={`${PRESENTATION_TEST_PREFIX}-default-levels-select`}
              inputOnly={false}
              value={curDefLevels}
              placeholder={curDefLevels}
              options={createSelectOptions(AllowedLevels, setDefaultLevels)}
              {...SharedSelectProperties}
            />
          </Flex>
          <Flex direction={"row"} gap={"16px"}>
            <Flex direction={"column"} width={"400px"}>
              <Select
                value={chartTypeIntToString(curChartType)}
                id={"chart-type-select"}
                label={appstr("Chart type")}
                dataTestId={`${PRESENTATION_TEST_PREFIX}-chart-type-select`}
                inputOnly={false}
                placeholder={chartTypeIntToString(curChartType)}
                options={createSelectOptions(chartTypes, setChartType, undefined, true)}
                selectSize={"medium"}
                {...SharedSelectProperties}
              />
            </Flex>
            {curChartType === enumChartTypes.level && (
              <Flex direction={"column"} width={"400px"}>
                <Select
                  value={chartConfig?.levelField}
                  id={"level-field-select"}
                  label={`${appstr("Level")} ${appstr("Field").toLowerCase()}`}
                  dataTestId={`${PRESENTATION_TEST_PREFIX}-level-field-select`}
                  inputOnly={false}
                  options={createSelectOptions(
                    chartConfig?.headers ?? [],
                    setLevelField,
                    "level-field-option-"
                  )}
                  selectSize={"medium"}
                  listProps={FieldSelectListProps()}
                  tooltipProps={{ message: appstr("levelcharthelp"), position: "bottom-start" }}
                  {...SharedSelectProperties}
                />
              </Flex>
            )}
          </Flex>
          <Flex direction={"column"} width={"400px"}>
            <Input
              style={{ marginBottom: "0" }}
              label={appstr("Max box limit for single page")}
              id={"page-single-page-limit-input"}
              dataTestId={`${PRESENTATION_TEST_PREFIX}-single-page-limit-input`}
              size="medium"
              value={chartConfig.maxQueryLimit[0]}
              onChange={e => HandleLimitInput(0, e)}
            />
          </Flex>
          <Flex direction={"column"} width={"400px"}>
            <Input
              style={{ marginBottom: "0" }}
              label={appstr("maxplimit")}
              id={"page-multi-page-limit-input"}
              dataTestId={`${PRESENTATION_TEST_PREFIX}-multi-page-limit-input`}
              size="medium"
              value={chartConfig.maxQueryLimit[1]}
              onChange={e => HandleLimitInput(1, e)}
            />
          </Flex>
        </Flex>
      </SectionCard>
      <SectionCard title={appstr("Sort")}>
        <BoxSort
          manager={sortConfig}
          setter={handleSortUpdate}
          shouldPerformSortCheckpoint={false}
          chartMetadata={chartConfig}
          dataTestIdPrefix={`${PRESENTATION_TEST_PREFIX}-sort`}
        />
      </SectionCard>
    </SectionCardGroup>
  );
};
